import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useSiteAdmin } from "@/hooks/useSiteAdmin";
import { useGlobalAdmin } from "@/hooks/useGlobalAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, Search, Upload, CheckCircle, XCircle, Clock, AlertCircle, ExternalLink } from "lucide-react";

interface UrlSubmission {
  id: string;
  url: string;
  url_type: string;
  status: string;
  last_checked_at: string | null;
  last_submitted_at: string | null;
  indexed_at: string | null;
  error_message: string | null;
  created_at: string;
}

export default function SearchConsole() {
  const navigate = useNavigate();
  const { site, loading: siteLoading } = useSiteAdmin();
  const { isGlobalAdmin } = useGlobalAdmin();
  const [submissions, setSubmissions] = useState<UrlSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [populating, setPopulating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googlePropertyId, setGooglePropertyId] = useState("");
  const [saving, setSaving] = useState(false);
  
  const siteId = site?.id;
  const [stats, setStats] = useState({ total: 0, pending: 0, submitted: 0, indexed: 0, error: 0 });

  useEffect(() => {
    if (!siteLoading && !siteId && !isGlobalAdmin) {
      navigate("/admin/login");
    }
  }, [siteId, siteLoading, isGlobalAdmin, navigate]);

  useEffect(() => {
    if (siteId) {
      fetchSubmissions();
      fetchSiteSettings();
    }
  }, [siteId]);

  const fetchSiteSettings = async () => {
    if (!siteId) return;
    
    const { data } = await supabase
      .from("sites")
      .select("google_property_id")
      .eq("id", siteId)
      .single();
    
    if (data?.google_property_id) {
      setGooglePropertyId(data.google_property_id);
    }
  };

  const fetchSubmissions = async () => {
    if (!siteId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("gsc_url_submissions")
        .select("*")
        .eq("site_id", siteId)
        .order("created_at", { ascending: false })
        .limit(500);

      if (error) throw error;

      setSubmissions(data || []);
      
      // Calculate stats
      const total = data?.length || 0;
      const pending = data?.filter(s => s.status === "pending").length || 0;
      const submitted = data?.filter(s => s.status === "submitted").length || 0;
      const indexed = data?.filter(s => s.status === "indexed").length || 0;
      const errorCount = data?.filter(s => s.status === "error").length || 0;
      
      setStats({ total, pending, submitted, indexed, error: errorCount });
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load URL submissions");
    } finally {
      setLoading(false);
    }
  };

  const savePropertyId = async () => {
    if (!siteId) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from("sites")
        .update({ google_property_id: googlePropertyId })
        .eq("id", siteId);

      if (error) throw error;
      toast.success("Google property ID saved");
    } catch (error) {
      console.error("Error saving property ID:", error);
      toast.error("Failed to save property ID");
    } finally {
      setSaving(false);
    }
  };

  const populateUrls = async () => {
    if (!siteId) return;
    
    setPopulating(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gsc-submit-urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ siteId, action: "populate_urls" }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || "Failed to populate URLs");
      }

      toast.success(result.message || "URLs populated successfully");
      await fetchSubmissions();
    } catch (error) {
      console.error("Error populating URLs:", error);
      toast.error(error instanceof Error ? error.message : "Failed to populate URLs");
    } finally {
      setPopulating(false);
    }
  };

  const submitPendingUrls = async () => {
    const pendingUrls = submissions.filter(s => s.status === "pending").map(s => s.url);
    
    if (pendingUrls.length === 0) {
      toast.info("No pending URLs to submit");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gsc-submit-urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ 
            siteId, 
            action: "submit_batch",
            urls: pendingUrls.slice(0, 100) // Limit to 100 at a time
          }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || "Failed to submit URLs");
      }

      const successCount = result.results?.filter((r: any) => r.success).length || 0;
      toast.success(`Submitted ${successCount} URLs for indexing`);
      await fetchSubmissions();
    } catch (error) {
      console.error("Error submitting URLs:", error);
      toast.error(error instanceof Error ? error.message : "Failed to submit URLs");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "indexed":
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" /> Indexed</Badge>;
      case "submitted":
        return <Badge className="bg-primary"><Clock className="w-3 h-3 mr-1" /> Submitted</Badge>;
      case "error":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default:
        return <Badge variant="secondary"><AlertCircle className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (siteLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Google Search Console</h1>
          <p className="text-muted-foreground">
            Manage URL indexing for Google Search
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total URLs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary">{stats.submitted}</div>
              <div className="text-sm text-muted-foreground">Submitted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-emerald-600">{stats.indexed}</div>
              <div className="text-sm text-muted-foreground">Indexed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-destructive">{stats.error}</div>
              <div className="text-sm text-muted-foreground">Errors</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="urls" className="space-y-4">
          <TabsList>
            <TabsTrigger value="urls">URL Submissions</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="urls" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>URL Submissions</CardTitle>
                    <CardDescription>
                      Track and manage URLs submitted to Google for indexing
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchSubmissions}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    <Button variant="outline" onClick={populateUrls} disabled={populating}>
                      {populating ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Discover URLs
                    </Button>
                    <Button onClick={submitPendingUrls} disabled={submitting || stats.pending === 0}>
                      {submitting ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Submit Pending ({stats.pending})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {submissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No URLs tracked yet</p>
                    <p className="text-sm">Click "Discover URLs" to populate all your site's pages</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>URL</TableHead>
                          <TableHead className="w-24">Type</TableHead>
                          <TableHead className="w-28">Status</TableHead>
                          <TableHead className="w-36">Last Submitted</TableHead>
                          <TableHead className="w-36">Indexed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submissions.map((submission) => (
                          <TableRow key={submission.id}>
                            <TableCell className="font-mono text-xs max-w-md truncate">
                              <a 
                                href={submission.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:underline flex items-center gap-1"
                              >
                                {submission.url.replace(/^https?:\/\/[^/]+/, "")}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {submission.url_type}
                              </Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(submission.status)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {formatDate(submission.last_submitted_at)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {formatDate(submission.indexed_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Google Search Console Settings</CardTitle>
                <CardDescription>
                  Configure your Google Search Console integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="propertyId">Google Property ID</Label>
                  <Input
                    id="propertyId"
                    placeholder="sc-domain:example.com or https://example.com/"
                    value={googlePropertyId}
                    onChange={(e) => setGooglePropertyId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in Google Search Console → Settings → Property Settings.
                    For domain properties, use format: sc-domain:yourdomain.com
                  </p>
                </div>
                <Button onClick={savePropertyId} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Service Account Status</CardTitle>
                <CardDescription>
                  Google service account for automated URL indexing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-600">
                    <CheckCircle className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Service account configured
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  The Google Indexing API is configured with a service account. URLs will be automatically
                  submitted to Google for indexing.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Setup Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-2">
                  <h4 className="font-medium">1. Add Service Account to Search Console</h4>
                  <p className="text-muted-foreground">
                    Go to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Search Console</a> → 
                    Settings → Users and permissions → Add user
                  </p>
                  <p className="text-muted-foreground">
                    Add this email as <strong>Owner</strong>:
                  </p>
                  <code className="block bg-muted px-3 py-2 rounded text-xs break-all">
                    st-real-estate-service-acct@sales-trends-network.iam.gserviceaccount.com
                  </code>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">2. Set Google Property ID</h4>
                  <p className="text-muted-foreground">
                    Enter your property ID above (e.g., <code className="bg-muted px-1 rounded">sc-domain:yourdomain.com</code>)
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">3. Discover & Submit URLs</h4>
                  <p className="text-muted-foreground">
                    Click "Discover URLs" to find all your site pages, then "Submit Pending" to request indexing.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">4. Automatic Daily Submissions</h4>
                  <p className="text-muted-foreground">
                    Pending URLs are automatically submitted daily via the batch job (up to 200/day due to API limits).
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
